import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdSense from 'react-adsense';
import './styles/Waiting.css';

const backendBaseUrl = process.env.REACT_APP_BACKEND_URL;
const backendURL = backendBaseUrl
  ? `${backendBaseUrl.replace(/\/$/, '')}/run`
  : null;
const FORM_COOKIE_KEY = 'dd214_form_data';

const clearSavedFormCookie = () => {
  document.cookie = `${FORM_COOKIE_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
};

const readStreamedTextResponse = async (response) => {
  if (!response.body || typeof response.body.getReader !== 'function') {
    return '';
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let accumulated = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    accumulated += decoder.decode(value, { stream: true });
  }

  accumulated += decoder.decode();
  return accumulated;
};

function Waiting() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [showServerErrorModal, setShowServerErrorModal] = useState(false);

  const inspirationalTexts = [
    "Your service built your foundation — now let's build your future.",
    "From uniform to unlimited: know your true earning potential.",
    "You're not starting over — you're starting from strength.",
    "The uniform may come off, but your value doesn't.",
    "Make the move that brings you closer to the life — and the people — you care about most."
  ];

  // Rotate carousel text every 4 seconds
  useEffect(() => {
    const textInterval = setInterval(() => {
      setCurrentTextIndex((prev) => (prev + 1) % inspirationalTexts.length);
    }, 4000);

    return () => clearInterval(textInterval);
  }, [inspirationalTexts.length]);

  // Fetch backend data on component mount
  useEffect(() => {
    const fetchData = async () => {
      const formData = location.state?.formData;
      
      if (!formData) {
        // Redirect to form if no data is present
        navigate('/questionnaire');
        return;
      }

      // Make POST request with JSON body instead of query string
      try {
        if (!backendURL) {
          throw new Error('Missing REACT_APP_BACKEND_URL environment variable');
        }

        const response = await fetch(backendURL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData)
        });
        
        if (!response.ok) {
          console.log(response.status);
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get('content-type') || '';
        let parsedResults;

        if (contentType.includes('application/json')) {
          const data = await response.json();
          parsedResults = data.html || data;
        } else {
          const streamedText = await readStreamedTextResponse(response);
          const trimmed = streamedText.trim();

          if (!trimmed) {
            throw new Error('Empty streamed response from backend');
          }

          try {
            const maybeJson = JSON.parse(trimmed);
            parsedResults = maybeJson.html || maybeJson;
          } catch {
            parsedResults = trimmed;
          }
        }

        console.log('Backend response:', parsedResults);

        clearSavedFormCookie();
        
        // Navigate to results page with both the backend analysis and original form data
        navigate('/results', {
          state: {
            results: parsedResults,
            formData: formData
          }
        });
      } catch (error) {
        console.error('Error:', error);
        setShowServerErrorModal(true);
      }
    };

    fetchData();
  }, [location.state, navigate]);

  return (
    <div className="waiting-page">
      <div className="waiting-bg" />

      <div className="waiting-shell" id='ad-shell'>
        <div className="waiting-ad">
          <div className="waiting-ad-text">
            <AdSense.Google
              client="ca-pub-XXXXXXXXXXXXXXXX"
              slot="XXXXXXXXXX"
              style={{ display: 'block' }}
              format="auto"
              responsive="true"
            />
          </div>
        </div>

        <div className="waiting-loader-wrap">
          <div className="waiting-loader" />
          
          <div className="waiting-title">
            <div className="loading"> Analyzing Your Profile <span></span><span className="dots"></span></div>
            <p></p>
            <p6>Usually takes 1 to 2 minutes to load</p6>
          </div>
        </div>

        <div className="waiting-carousel">
          {inspirationalTexts.map((text, index) => (
            <div
              key={index}
              className={`waiting-quote ${currentTextIndex === index ? 'active' : ''}`}
            >
              "{text}"
            </div>
          ))}
        </div>

        <div className="waiting-dots">
          {}
        </div>
      </div>

      {showServerErrorModal && (
        <div className="waiting-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="server-error-modal-title">
          <div className="waiting-modal-card">
            <h2 id="server-error-modal-title" className="waiting-modal-title">Connection Issue</h2>
            <p className="waiting-modal-text">
              There are issues connecting with the server. Please try again later.
            </p>
            <button
              type="button"
              className="waiting-modal-btn"
              onClick={() => {
                setShowServerErrorModal(false);
                navigate('/questionnaire');
              }}
            >
              Back to Questionnaire
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Waiting;
